import { useContext, useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { AppContext } from "../context/app.context";
import {
  MONGODB_AWSGW_QUERIES,
  MONGODB_AWSGW_APIKEY,
  MONGODB_DATASOURCE_TS,
  MONGODB_COLLECTION_OFFICES,
} from "../utility/keys";
import { buildRegionLocationMap } from "../utility/buildRegionLocationMap";
import { ModelCxContext } from "../context/modelCx.context";
import { resolveBackendMode } from "../utility/backendConfig";
import { refs } from "../convex/apiRefs";
import { convexReadEnvelope } from "../convex/convexRead";

// - - - - - - - - - -
// #139 Phase 8.3 — fetch the office docs for every accessible location so
// we can derive the region → location-codes map used by the region
// filter pills.
//
// Fires one getOfficeMetaDataByLocation per location in parallel via
// useQueries. The Lambda's existing single-location findOne is reused
// (no new Lambda method needed); a future `getOfficesByCompany` could
// replace this with a single call by swapping the queryFn here while
// keeping the hook's return shape identical.
//
// Cache strategy: staleTime 5 minutes (offices rarely change), no
// refetchOnWindowFocus / refetchInterval (snapshot semantics, like the
// existing useDashboardOpenings). queryKey is prefixed with "dashboard:"
// so we never collide with the Optimizer's getOfficeMetaDataByLocation
// (which is single-location and has its own caching needs).
//
// Returns:
//   {
//     regionList: Array<{ region: string, locations: string[] }>
//                                          // sorted; built from successful fetches only
//     isLoading: boolean                   // any query still in flight
//     isError:   boolean                   // any query failed
//     officeDocsByLocation: Record<string, OfficeDoc|null>
//                                          // exposed for diagnostics / extensions
//   }

async function fetchOfficeLambda(body) {
  const response = await fetch(MONGODB_AWSGW_QUERIES, {
    method: "POST",
    mode: "cors",
    credentials: "omit",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-api-key": MONGODB_AWSGW_APIKEY,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(
      `useDashboardRegions HTTP ${response.status}: ${response.statusText}`
    );
  }
  const result = await response.json();
  if (result.errorType || result.errorMessage) {
    throw new Error(
      result.errorMessage || result.errorType || "Unknown error"
    );
  }
  return result;
}

export function useDashboardRegions({ locations, enabled = true }) {
  const { appEnvirons } = useContext(AppContext);
  const { tsUserDocument } = useContext(ModelCxContext);
  const isConvexOnly = resolveBackendMode(tsUserDocument) === "convexOnly";
  const mongoDatabase = appEnvirons?.mongoDb;

  const safeLocations = useMemo(
    () => (Array.isArray(locations) ? locations : []),
    [locations]
  );

  const queries = useMemo(() => {
    if (!enabled || (!isConvexOnly && !mongoDatabase)) return [];
    return safeLocations.map((locationCode) => ({
      queryKey: [
        "dashboard:getOfficeMetaDataByLocation",
        locationCode,
      ],
      queryFn: () =>
        isConvexOnly
          ? convexReadEnvelope(
              refs.getOfficeMetaDataByLocation,
              { locationCode },
              "doc"
            )
          : fetchOfficeLambda({
              method: "getOfficeMetaDataByLocation",
              dataSource: MONGODB_DATASOURCE_TS,
              database: mongoDatabase,
              collection: MONGODB_COLLECTION_OFFICES,
              locationCode,
            }),
      enabled: !!locationCode,
      staleTime: 5 * 60_000, // 5 min — offices rarely change
      refetchOnWindowFocus: false,
      // Match useDashboardOpenings: close + reopen the modal = fresh
      // fetch. The cost is N small office findOne calls per open, which
      // is small. Picks up new `Region` values as the bulk-push fix-ups
      // land on the 22 currently-empty offices.
      refetchOnMount: "always",
    }));
  }, [mongoDatabase, safeLocations, enabled, isConvexOnly]);

  const results = useQueries({ queries });

  const officeDocsByLocation = useMemo(() => {
    const map = {};
    safeLocations.forEach((locationCode, i) => {
      const result = results[i] || {};
      // Lambda response shape: raw.message.result === single office doc
      map[locationCode] = result?.data?.message?.result ?? null;
    });
    return map;
  }, [results, safeLocations]);

  const regionList = useMemo(
    () => buildRegionLocationMap(officeDocsByLocation),
    [officeDocsByLocation]
  );

  const isLoading = useMemo(
    () => results.some((r) => r && r.isLoading),
    [results]
  );
  const isError = useMemo(() => results.some((r) => r && r.isError), [results]);

  return { regionList, isLoading, isError, officeDocsByLocation };
}
